import React from 'react';
import { cx } from '@compiled/react';

import Pressable from '@atlaskit/primitives/pressable';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { IconProp } from '@atlaskit/button/new';

const styles = cssMap({
	base: {
		// default Atlaskit button styles
		display: 'inline-flex',
		boxSizing: 'border-box',
		width: 'auto',
		maxWidth: '100%',
		position: 'relative',
		alignItems: 'center', // override default: baseline
		justifyContent: 'center',
		columnGap: token('space.050'),
		borderRadius: token('radius.small'),
		borderWidth: 0,
		flexShrink: 0,
		height: '2rem',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		paddingBlock: token('space.075'),
		paddingInlineEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		textAlign: 'center',
		transition: 'background 0.1s ease-out',
		verticalAlign: 'middle',
	},
	iconButton: {
		// default Atlaskit icon button styles
		height: '2rem',
		width: '2rem',
		paddingInlineEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
	},
	disabled: {
		// default Atlaskit button styles
		cursor: 'not-allowed',
		backgroundColor: token('color.background.neutral.subtle'), // override default: color.background.disabled
		color: token('color.text.disabled'),
	},
	subtle: {
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

export type AudioControlIconButtonProps = {
	Icon: IconProp;
	tooltip: string;
	label: string;
	onClick?: () => void;
	testId?: string;
	isDisabled?: boolean;
};

export type AudioControlTriggerButtonProps = {
	children: React.ReactNode;
	triggerRef: React.Ref<any>;
	triggerProps: React.ButtonHTMLAttributes<HTMLButtonElement>;
	onClick?: () => void;
	testId?: string;
	isDisabled?: boolean;
};

export const AudioControlIconButton = ({
	onClick = () => {},
	Icon,
	tooltip,
	label,
	testId,
	isDisabled,
}: AudioControlIconButtonProps) => {
	return (
		<Tooltip content={!isDisabled && tooltip}>
			<Pressable
				onClick={onClick}
				isDisabled={isDisabled}
				xcss={cx(styles.base, styles.iconButton, isDisabled ? styles.disabled : styles.subtle)}
				testId={testId}
				aria-label={label}
			>
				<Icon
					label={label}
					color={isDisabled ? token('color.icon.disabled') : token('color.icon.subtle')}
				/>
			</Pressable>
		</Tooltip>
	);
};

export const AudioControlTriggerButton = ({
	onClick = () => {},
	testId,
	isDisabled,
	children,
	triggerRef,
	triggerProps,
}: AudioControlTriggerButtonProps) => {
	return (
		<Pressable
			onClick={onClick}
			isDisabled={isDisabled}
			xcss={cx(styles.base, isDisabled ? styles.disabled : styles.subtle)}
			testId={testId}
			ref={triggerRef}
			{...triggerProps}
		>
			{children}
		</Pressable>
	);
};
